// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

// Code generated by Microsoft (R) AutoRest Code Generator.
// Changes may cause incorrect behavior and will be lost if the code is regenerated.

import type { PagedAsyncIterableIterator, PageSettings } from "@azure/core-paging";
import { setContinuationToken } from "../pagingHelper.js";
import type { StaticCidrs } from "../operationsInterfaces/index.js";
import * as coreClient from "@azure/core-client";
import * as Mappers from "../models/mappers.js";
import * as Parameters from "../models/parameters.js";
import type { NetworkManagementClient } from "../networkManagementClient.js";
import type { SimplePollerLike, OperationState } from "@azure/core-lro";
import { createHttpPoller } from "@azure/core-lro";
import { createLroSpec } from "../lroImpl.js";
import type {
  StaticCidr,
  StaticCidrsListNextOptionalParams,
  StaticCidrsListOptionalParams,
  StaticCidrsListResponse,
  StaticCidrsCreateOptionalParams,
  StaticCidrsCreateResponse,
  StaticCidrsGetOptionalParams,
  StaticCidrsGetResponse,
  StaticCidrsDeleteOptionalParams,
  StaticCidrsDeleteResponse,
  StaticCidrsListNextResponse,
} from "../models/index.js";

/// <reference lib="esnext.asynciterable" />
/** Class containing StaticCidrs operations. */
export class StaticCidrsImpl implements StaticCidrs {
  private readonly client: NetworkManagementClient;

  /**
   * Initialize a new instance of the class StaticCidrs class.
   * @param client Reference to the service client
   */
  constructor(client: NetworkManagementClient) {
    this.client = client;
  }

  /**
   * Gets list of Static CIDR resources at Network Manager level.
   * @param resourceGroupName The name of the resource group.
   * @param networkManagerName The name of the network manager.
   * @param poolName Pool resource name.
   * @param options The options parameters.
   */
  public list(
    resourceGroupName: string,
    networkManagerName: string,
    poolName: string,
    options?: StaticCidrsListOptionalParams,
  ): PagedAsyncIterableIterator<StaticCidr> {
    const iter = this.listPagingAll(resourceGroupName, networkManagerName, poolName, options);
    return {
      next() {
        return iter.next();
      },
      [Symbol.asyncIterator]() {
        return this;
      },
      byPage: (settings?: PageSettings) => {
        if (settings?.maxPageSize) {
          throw new Error("maxPageSize is not supported by this operation.");
        }
        return this.listPagingPage(
          resourceGroupName,
          networkManagerName,
          poolName,
          options,
          settings,
        );
      },
    };
  }

  private async *listPagingPage(
    resourceGroupName: string,
    networkManagerName: string,
    poolName: string,
    options?: StaticCidrsListOptionalParams,
    settings?: PageSettings,
  ): AsyncIterableIterator<StaticCidr[]> {
    let result: StaticCidrsListResponse;
    let continuationToken = settings?.continuationToken;
    if (!continuationToken) {
      result = await this._list(resourceGroupName, networkManagerName, poolName, options);
      const page = result.value || [];
      continuationToken = result.nextLink;
      setContinuationToken(page, continuationToken);
      yield page;
    }
    while (continuationToken) {
      result = await this._listNext(
        resourceGroupName,
        networkManagerName,
        poolName,
        continuationToken,
        options,
      );
      continuationToken = result.nextLink;
      const page = result.value || [];
      setContinuationToken(page, continuationToken);
      yield page;
    }
  }

  private async *listPagingAll(
    resourceGroupName: string,
    networkManagerName: string,
    poolName: string,
    options?: StaticCidrsListOptionalParams,
  ): AsyncIterableIterator<StaticCidr> {
    for await (const page of this.listPagingPage(
      resourceGroupName,
      networkManagerName,
      poolName,
      options,
    )) {
      yield* page;
    }
  }

  /**
   * Gets list of Static CIDR resources at Network Manager level.
   * @param resourceGroupName The name of the resource group.
   * @param networkManagerName The name of the network manager.
   * @param poolName Pool resource name.
   * @param options The options parameters.
   */
  private _list(
    resourceGroupName: string,
    networkManagerName: string,
    poolName: string,
    options?: StaticCidrsListOptionalParams,
  ): Promise<StaticCidrsListResponse> {
    return this.client.sendOperationRequest(
      { resourceGroupName, networkManagerName, poolName, options },
      listOperationSpec,
    );
  }

  /**
   * Creates/Updates the Static CIDR resource.
   * @param resourceGroupName The name of the resource group.
   * @param networkManagerName The name of the network manager.
   * @param poolName IP Address Manager Pool resource name.
   * @param staticCidrName Static Cidr allocation name.
   * @param options The options parameters.
   */
  create(
    resourceGroupName: string,
    networkManagerName: string,
    poolName: string,
    staticCidrName: string,
    options?: StaticCidrsCreateOptionalParams,
  ): Promise<StaticCidrsCreateResponse> {
    return this.client.sendOperationRequest(
      {
        resourceGroupName,
        networkManagerName,
        poolName,
        staticCidrName,
        options,
      },
      createOperationSpec,
    );
  }

  /**
   * Gets the specific Static CIDR resource.
   * @param resourceGroupName The name of the resource group.
   * @param networkManagerName The name of the network manager.
   * @param poolName Pool resource name.
   * @param staticCidrName StaticCidr resource name to retrieve.
   * @param options The options parameters.
   */
  get(
    resourceGroupName: string,
    networkManagerName: string,
    poolName: string,
    staticCidrName: string,
    options?: StaticCidrsGetOptionalParams,
  ): Promise<StaticCidrsGetResponse> {
    return this.client.sendOperationRequest(
      {
        resourceGroupName,
        networkManagerName,
        poolName,
        staticCidrName,
        options,
      },
      getOperationSpec,
    );
  }

  /**
   * Delete the Static CIDR resource.
   * @param resourceGroupName The name of the resource group.
   * @param networkManagerName The name of the network manager.
   * @param poolName Pool resource name.
   * @param staticCidrName StaticCidr resource name to delete.
   * @param options The options parameters.
   */
  async beginDelete(
    resourceGroupName: string,
    networkManagerName: string,
    poolName: string,
    staticCidrName: string,
    options?: StaticCidrsDeleteOptionalParams,
  ): Promise<
    SimplePollerLike<OperationState<StaticCidrsDeleteResponse>, StaticCidrsDeleteResponse>
  > {
    const directSendOperation = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ): Promise<StaticCidrsDeleteResponse> => {
      return this.client.sendOperationRequest(args, spec);
    };
    const sendOperationFn = async (
      args: coreClient.OperationArguments,
      spec: coreClient.OperationSpec,
    ) => {
      let currentRawResponse: coreClient.FullOperationResponse | undefined = undefined;
      const providedCallback = args.options?.onResponse;
      const callback: coreClient.RawResponseCallback = (
        rawResponse: coreClient.FullOperationResponse,
        flatResponse: unknown,
      ) => {
        currentRawResponse = rawResponse;
        providedCallback?.(rawResponse, flatResponse);
      };
      const updatedArgs = {
        ...args,
        options: {
          ...args.options,
          onResponse: callback,
        },
      };
      const flatResponse = await directSendOperation(updatedArgs, spec);
      return {
        flatResponse,
        rawResponse: {
          statusCode: currentRawResponse!.status,
          body: currentRawResponse!.parsedBody,
          headers: currentRawResponse!.headers.toJSON(),
        },
      };
    };

    const lro = createLroSpec({
      sendOperationFn,
      args: {
        resourceGroupName,
        networkManagerName,
        poolName,
        staticCidrName,
        options,
      },
      spec: deleteOperationSpec,
    });
    const poller = await createHttpPoller<
      StaticCidrsDeleteResponse,
      OperationState<StaticCidrsDeleteResponse>
    >(lro, {
      restoreFrom: options?.resumeFrom,
      intervalInMs: options?.updateIntervalInMs,
      resourceLocationConfig: "location",
    });
    await poller.poll();
    return poller;
  }

  /**
   * Delete the Static CIDR resource.
   * @param resourceGroupName The name of the resource group.
   * @param networkManagerName The name of the network manager.
   * @param poolName Pool resource name.
   * @param staticCidrName StaticCidr resource name to delete.
   * @param options The options parameters.
   */
  async beginDeleteAndWait(
    resourceGroupName: string,
    networkManagerName: string,
    poolName: string,
    staticCidrName: string,
    options?: StaticCidrsDeleteOptionalParams,
  ): Promise<StaticCidrsDeleteResponse> {
    const poller = await this.beginDelete(
      resourceGroupName,
      networkManagerName,
      poolName,
      staticCidrName,
      options,
    );
    return poller.pollUntilDone();
  }

  /**
   * ListNext
   * @param resourceGroupName The name of the resource group.
   * @param networkManagerName The name of the network manager.
   * @param poolName Pool resource name.
   * @param nextLink The nextLink from the previous successful call to the List method.
   * @param options The options parameters.
   */
  private _listNext(
    resourceGroupName: string,
    networkManagerName: string,
    poolName: string,
    nextLink: string,
    options?: StaticCidrsListNextOptionalParams,
  ): Promise<StaticCidrsListNextResponse> {
    return this.client.sendOperationRequest(
      { resourceGroupName, networkManagerName, poolName, nextLink, options },
      listNextOperationSpec,
    );
  }
}
// Operation Specifications
const serializer = coreClient.createSerializer(Mappers, /* isXml */ false);

const listOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Network/networkManagers/{networkManagerName}/ipamPools/{poolName}/staticCidrs",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.StaticCidrList,
    },
    default: {
      bodyMapper: Mappers.CommonErrorResponse,
    },
  },
  queryParameters: [
    Parameters.apiVersion,
    Parameters.skipToken,
    Parameters.skip,
    Parameters.top1,
    Parameters.sortKey,
    Parameters.sortValue,
  ],
  urlParameters: [
    Parameters.$host,
    Parameters.resourceGroupName,
    Parameters.subscriptionId,
    Parameters.networkManagerName1,
    Parameters.poolName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const createOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Network/networkManagers/{networkManagerName}/ipamPools/{poolName}/staticCidrs/{staticCidrName}",
  httpMethod: "PUT",
  responses: {
    200: {
      bodyMapper: Mappers.StaticCidr,
    },
    201: {
      bodyMapper: Mappers.StaticCidr,
    },
    default: {
      bodyMapper: Mappers.CommonErrorResponse,
    },
  },
  requestBody: Parameters.body2,
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.$host,
    Parameters.resourceGroupName,
    Parameters.subscriptionId,
    Parameters.networkManagerName1,
    Parameters.poolName,
    Parameters.staticCidrName,
  ],
  headerParameters: [Parameters.accept, Parameters.contentType],
  mediaType: "json",
  serializer,
};
const getOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Network/networkManagers/{networkManagerName}/ipamPools/{poolName}/staticCidrs/{staticCidrName}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.StaticCidr,
    },
    default: {
      bodyMapper: Mappers.CommonErrorResponse,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.$host,
    Parameters.resourceGroupName,
    Parameters.subscriptionId,
    Parameters.networkManagerName1,
    Parameters.poolName,
    Parameters.staticCidrName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const deleteOperationSpec: coreClient.OperationSpec = {
  path: "/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.Network/networkManagers/{networkManagerName}/ipamPools/{poolName}/staticCidrs/{staticCidrName}",
  httpMethod: "DELETE",
  responses: {
    200: {
      headersMapper: Mappers.StaticCidrsDeleteHeaders,
    },
    201: {
      headersMapper: Mappers.StaticCidrsDeleteHeaders,
    },
    202: {
      headersMapper: Mappers.StaticCidrsDeleteHeaders,
    },
    204: {
      headersMapper: Mappers.StaticCidrsDeleteHeaders,
    },
    default: {
      bodyMapper: Mappers.CommonErrorResponse,
    },
  },
  queryParameters: [Parameters.apiVersion],
  urlParameters: [
    Parameters.$host,
    Parameters.resourceGroupName,
    Parameters.subscriptionId,
    Parameters.networkManagerName1,
    Parameters.poolName,
    Parameters.staticCidrName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
const listNextOperationSpec: coreClient.OperationSpec = {
  path: "{nextLink}",
  httpMethod: "GET",
  responses: {
    200: {
      bodyMapper: Mappers.StaticCidrList,
    },
    default: {
      bodyMapper: Mappers.CommonErrorResponse,
    },
  },
  urlParameters: [
    Parameters.$host,
    Parameters.resourceGroupName,
    Parameters.subscriptionId,
    Parameters.nextLink,
    Parameters.networkManagerName1,
    Parameters.poolName,
  ],
  headerParameters: [Parameters.accept],
  serializer,
};
